import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoginMutation,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import { loginSuccess } from "../redux/slices/authSlice";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import { setSettings } from "../redux/slices/homepageSlice";
import {
  selectSettings,
  selectHomepageLoading,
} from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaInitialized = useRef(false);

  // Check if reCAPTCHA is configured
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const isRecaptchaEnabled = recaptchaSiteKey && recaptchaSiteKey.trim() !== "";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const settings = useSelector(selectSettings);
  const settingsLoading = useSelector(selectHomepageLoading);

  // RTK Query hooks
  const [login, { isLoading: loading }] = useLoginMutation();
  const { data: settingsData } = useGetHomepageSettingsQuery();

  // Load settings into Redux when fetched
  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleInputChange = (field, value) => {
    if (error) {
      setError(""); // Clear error when user starts typing
    }
    if (recaptchaError) {
      setRecaptchaError(false);
    }

    if (field === "email") {
      setEmail(value);
    } else if (field === "password") {
      setPassword(value);
    } else if (field === "remember") {
      setRemember(value);
    }
  };

  const handleRecaptchaChange = useCallback((token) => {
    if (token) {
      setRecaptchaToken(token);
      setRecaptchaError(false);
      // Always clear error when reCAPTCHA token is received
      setError("");
    }
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaToken("");
    setRecaptchaError(false);
    setError("");
  }, []);

  const handleRecaptchaError = useCallback(() => {
    setRecaptchaToken("");
    setRecaptchaError(true);
    setError(t("login.recaptchaFailed"));
  }, [t]);

  // Clear reCAPTCHA error when token is received
  useEffect(() => {
    if (recaptchaToken && recaptchaError) {
      setRecaptchaError(false);
      setError("");
    }
  }, [recaptchaToken, recaptchaError]);

  // Initialize reCAPTCHA when component mounts and site key is available
  useEffect(() => {
    if (!isRecaptchaEnabled || !recaptchaRef.current) return;

    // Check if already initialized
    if (recaptchaInitialized.current) {
      return;
    }

    // Wait for grecaptcha to be available
    let checkGrecaptcha = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.render) {
        clearInterval(checkGrecaptcha);

        // Double check if not already initialized
        if (recaptchaInitialized.current || !recaptchaRef.current) {
          return;
        }

        try {
          const widgetId = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: recaptchaSiteKey,
            callback: handleRecaptchaChange,
            "expired-callback": handleRecaptchaExpired,
            "error-callback": handleRecaptchaError,
          });

          // Store widget ID and mark as initialized
          recaptchaRef.current.widgetId = widgetId;
          recaptchaInitialized.current = true;
        } catch (error) {
          console.error("Error rendering reCAPTCHA:", error);
        }
      }
    }, 100);

    // Cleanup
    return () => {
      clearInterval(checkGrecaptcha);
      if (
        recaptchaInitialized.current &&
        recaptchaRef.current &&
        recaptchaRef.current.widgetId !== undefined &&
        window.grecaptcha
      ) {
        try {
          window.grecaptcha.reset(recaptchaRef.current.widgetId);
          recaptchaRef.current.widgetId = undefined;
          recaptchaInitialized.current = false;
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecaptchaEnabled, recaptchaSiteKey]);

  const handleSubmit = async () => {
    setError("");

    // Check if reCAPTCHA is completed (required if enabled)
    if (isRecaptchaEnabled && !recaptchaToken) {
      setRecaptchaError(true);
      setError(t("login.recaptchaRequired"));
      return;
    }

    setRecaptchaError(false);

    // Prepare login data
    const loginData = {
      email,
      password,
      remember,
    };

    // Always include reCAPTCHA token if available
    if (recaptchaToken) {
      loginData.recaptcha_token = recaptchaToken;
    }

    console.log("Login attempt:", {
      email,
      hasRecaptchaToken: !!recaptchaToken,
      recaptchaTokenLength: recaptchaToken ? recaptchaToken.length : 0,
      isRecaptchaEnabled,
    });

    try {
      const result = await login(loginData).unwrap();

      if (result.success && result.user) {
        // Reset reCAPTCHA
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.reset();
        }
        setRecaptchaToken("");

        // Update Redux state
        dispatch(
          loginSuccess({
            user: result.user,
            token: result.token,
          })
        );
        navigate(from, { replace: true });
      } else {
        setError(result.message || t("login.loginFailed"));
        // Reset reCAPTCHA on error
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.reset();
        }
        setRecaptchaToken("");
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err.data?.message;
      if (errorMessage === "Invalid credentials") {
        setError(t("login.invalidCredentials"));
      } else {
        setError(errorMessage || t("login.unexpectedError"));
      }
      // Reset reCAPTCHA on error
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.reset();
      }
      setRecaptchaToken("");
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form-wrapper">
          <div className="login-header">
            <div className="login-logo">
              {!settingsLoading && settings.branding.logo ? (
                <img
                  src={settings.branding.logo}
                  alt={settings.branding.companyName || "Company Logo"}
                  className="login-logo-image"
                />
              ) : (
                <div className="login-logo-placeholder">
                  {!settingsLoading && settings.branding.companyName
                    ? settings.branding.companyName.charAt(0).toUpperCase()
                    : "S"}
                </div>
              )}
            </div>
            <h2 className="login-title">{t("login.title")}</h2>
            <p className="login-subtitle">{t("login.subtitle")}</p>
          </div>

          <div className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t("login.email")}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
                className="form-input"
                placeholder={t("login.enterEmail")}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                {t("login.password")}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
                className="form-input"
                placeholder={t("login.enterPassword")}
                disabled={loading}
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) =>
                    handleInputChange("remember", e.target.checked)
                  }
                  disabled={loading}
                />
                <span className="checkbox-text">{t("login.rememberMe")}</span>
              </label>
            </div>

            {isRecaptchaEnabled && (
              <div className="form-group recaptcha-group">
                <div ref={recaptchaRef}></div>
              </div>
            )}

            <button
              type="button"
              className="login-button"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? t("login.signingIn") : t("login.signIn")}
            </button>
          </div>

          <div className="login-footer">
            <p className="login-footer-text">{t("login.welcomeMessage")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
